/**
 * Custom Cart Savings Script - Recharge Affinity Version
 * Overrides the "Saving X%" labels in the Recharge Shadow DOM cart drawer.
 */

(function() {
    /** @type {{ items?: Array<any> } | null} */
    let lastCartData = null;
    let hasRechargeObserver = false;

    /**
     * @param {unknown} value
     * @returns {boolean}
     */
    function isTruthyPropertyValue(value) {
        if (typeof value === 'boolean') return value;
        if (value == null) return false;
        const normalized = String(value).trim().toLowerCase();
        return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    /**
     * @param {{ properties?: any } | null} cartItem
     * @returns {boolean}
     */
    function shouldHideOneTimeForItem(cartItem) {
        if (!cartItem || !cartItem.properties) return false;

        const properties = cartItem.properties;

        if (Array.isArray(properties)) {
            const prop = properties.find((entry) => entry?.key === '_subscription_only' || entry?.name === '_subscription_only');
            return isTruthyPropertyValue(prop?.value);
        }

        return isTruthyPropertyValue(properties._subscription_only);
    }

    async function fetchCartData() {
        try {
            const response = await fetch('/cart.js');
            lastCartData = await response.json();
            return lastCartData;
        } catch (e) {
            console.error('Failed to fetch cart data', e);
            return null;
        }
    }

    /**
     * @param {ShadowRoot} shadowRoot
     */
    function updateRechargeSavings(shadowRoot) {
        if (!shadowRoot || !lastCartData) return;

        // The items in the Recharge drawer typically follow the order of items in cart.js
        const items = lastCartData.items || [];
        
        // Find all line item containers in the shadow DOM
        // Looking for elements that contain the price and savings info
        const savingLabels = shadowRoot.querySelectorAll('div.text-storefront-drawer-accent.font-semibold');
        
        // We need to find the specific "Saving X%" labels. 
        // Based on analysis, they have class: text-xs text-right text-storefront-drawer-accent font-semibold
        const targetLabels = Array.from(savingLabels).filter(el => el.textContent.includes('Saving'));

        targetLabels.forEach((label, index) => {
            // Match with cart items. This assumes order parity which is standard for these drawers.
            const cartItem = items[index];
            if (!cartItem) return;

            let savingPercent = cartItem.properties ? cartItem.properties._cart_saving_percent : null;
            
            // Handle array form of properties just in case
            if (!savingPercent && Array.isArray(cartItem.properties)) {
                const prop = cartItem.properties.find((/** @type {{ key?: string, name?: string }} */ entry) => entry.key === '_cart_saving_percent' || entry.name === '_cart_saving_percent');
                savingPercent = prop ? prop.value : null;
            }

            // Fallback for gifts (Free items)
            if (!savingPercent && (cartItem.price === 0 || cartItem.original_price === 0)) {
                savingPercent = '100';
            }

            if (savingPercent) {
                const newText = `Saving ${savingPercent}%`;
                if (label.textContent !== newText) {
                    label.textContent = newText;
                }
            }
        });
    }

    /**
     * @param {ShadowRoot} shadowRoot
     */
    function updateRechargeOneTimeOptionVisibility(shadowRoot) {
        if (!shadowRoot || !lastCartData) return;

        const items = lastCartData.items || [];
        const planSelects = Array.from(shadowRoot.querySelectorAll('select')).filter((select) =>
            select.querySelector('option[value="ONETIME"]')
        );

        planSelects.forEach((select, index) => {
            const cartItem = items[index];
            if (!cartItem) return;

            const oneTimeOption = /** @type {HTMLOptionElement | null} */ (select.querySelector('option[value="ONETIME"]'));
            if (!oneTimeOption) return;

            const shouldHide = shouldHideOneTimeForItem(cartItem);
            oneTimeOption.hidden = shouldHide;
            oneTimeOption.disabled = shouldHide;
            oneTimeOption.style.display = shouldHide ? 'none' : '';

            if (shouldHide && oneTimeOption.selected) {
                const nextEnabledOption = Array.from(select.options).find((option) => !option.disabled && option.value !== 'ONETIME');
                if (nextEnabledOption) {
                    select.value = nextEnabledOption.value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        });
    }

    function initRechargeObserver() {
        const shadowHost = document.querySelector('#recharge-storefront-experiences__cart-drawer');
        if (!shadowHost) return;

        const shadowRoot = shadowHost.shadowRoot;
        if (!shadowRoot) return;

        // MutationObserver for the shadow root to handle dynamic updates (item additions, quantity changes)
        const observer = new MutationObserver(async (mutations) => {
            // When something changes in the shadow DOM, we likely need fresh cart data
            await fetchCartData();
            updateRechargeSavings(shadowRoot);
            updateRechargeOneTimeOptionVisibility(shadowRoot);
        });

        observer.observe(shadowRoot, {
            childList: true,
            subtree: true
        });

        // Initial run
        fetchCartData().then(() => {
            updateRechargeSavings(shadowRoot);
            updateRechargeOneTimeOptionVisibility(shadowRoot);
        });
    }

    // Since the Recharge drawer script might load late, we check periodically
    const checkInterval = setInterval(() => {
        const shadowHost = document.querySelector('#recharge-storefront-experiences__cart-drawer');
        if (shadowHost && shadowHost.shadowRoot) {
            if (hasRechargeObserver) {
                clearInterval(checkInterval);
                return;
            }
            initRechargeObserver();
            hasRechargeObserver = true;
            clearInterval(checkInterval);
        }
    }, 1000);

    // Also listen for potential cart change events from the theme
    document.addEventListener('rebuy:cart.change', () => fetchCartData());
    document.addEventListener('cart:updated', () => fetchCartData());

})();


document.addEventListener('DOMContentLoaded', () => {
  const host = document.getElementById('recharge-storefront-experiences__cart-drawer');

  if (!host) {
    console.warn('Host element not found');
    return;
  }

  const shadowRoot = host.shadowRoot;

  if (!shadowRoot) {
    console.warn('shadowRoot not found');
    return;
  }

  let timeout = null;

  const observer = new MutationObserver(() => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      const hiddenProducts = (window.hiddenOTPProducts || '')
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);

      const items = shadowRoot.querySelectorAll(
        '.flex.flex-col.py-6.px-drawer.gap-4.border-storefront-drawer-border.border-b[role="listitem"]'
      );

      items.forEach((item) => {
        const titleEl = item.querySelector('.flex.flex-col .font-semibold');

        if (!titleEl) return;

        const title = titleEl.textContent.trim();

        if (hiddenProducts.includes(title)) {
          const select = item.querySelector('select');

          if (!select) return;

          const oneTimeOption = select.querySelector('option[value="ONETIME"]');

          if (oneTimeOption) {
            oneTimeOption.remove();
          }
        }
      });

    }, 150);
  });

  observer.observe(shadowRoot, {
    childList: true,
    subtree: true
  });
});